import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Search } from "lucide-react";
import { useSpring, animated } from "@react-spring/web";
import { createPortal } from "react-dom";
import { useSelectGitRepo } from "@/hooks/git";
import type { GitRepo } from "@/services/git/types";

// ─── Repo Picker ──────────────────────────────────────────────────────────────

export function RepoPicker({
  selectedRepo,
  repos,
  reposLoading,
  onSelect,
}: {
  selectedRepo: GitRepo;
  repos: GitRepo[];
  reposLoading: boolean;
  onSelect: (repoId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { mutate: selectRepo } = useSelectGitRepo();

  function handleToggle() {
    if (!open && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
    setOpen((o) => !o);
    if (open) setQuery("");
  }

  function handleClose() {
    setOpen(false);
    setQuery("");
  }

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      const insideTrigger = triggerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) handleClose();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const backdropSpring = useSpring({
    opacity: open ? 1 : 0,
    config: { tension: 280, friction: 26, clamp: true },
  });

  const dropdownSpring = useSpring({
    opacity: open ? 1 : 0,
    transform: open ? "translateY(0px) scale(1)" : "translateY(-8px) scale(0.97)",
    config: { tension: 320, friction: 26, clamp: true },
  });

  const chevronSpring = useSpring({
    rotate: open ? 180 : 0,
    config: { tension: 320, friction: 26 },
  });

  const filtered = repos.filter((r) =>
    r.full_name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative w-full">
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="studio-button flex w-full items-center gap-2 text-left"
      >
        <span className="min-w-0 flex-1 truncate font-mono text-xs font-semibold">
          {selectedRepo.name}
        </span>
        {selectedRepo.private && (
          <span className="shrink-0 rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-purple-400">
            private
          </span>
        )}
        <animated.div style={{ rotate: chevronSpring.rotate }} className="shrink-0">
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </animated.div>
      </button>

      {createPortal(
        <>
          {/* Backdrop — starts below the header so it never blurs header content */}
          <animated.div
            style={{
              ...backdropSpring,
              pointerEvents: open ? "auto" : "none",
              position: "fixed",
              top: rect ? rect.bottom + 8 : 52,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 40,
            }}
            className="backdrop-blur-[2px]"
            onClick={handleClose}
          />

          {/* Dropdown — portalled so it sits above the backdrop */}
          <animated.div
            ref={dropdownRef}
            style={{
              ...dropdownSpring,
              pointerEvents: open ? "auto" : "none",
              position: "fixed",
              top: rect ? rect.bottom + 6 : 0,
              left: rect ? rect.left : 0,
              width: 320,
            }}
            className="z-50 overflow-hidden rounded-xl border border-border/60 bg-popover shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
          >
            {/* Search */}
            <div className="border-b border-border/60 px-3 py-2.5">
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-2.5 py-1.5">
                <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search repositories…"
                  className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
                />
              </div>
            </div>

            {/* Repo list */}
            <div className="max-h-64 overflow-y-auto py-1">
              {reposLoading ? (
                <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Loading…
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-xs text-muted-foreground">No repositories found</p>
              ) : (
                filtered.map((repo) => (
                  <button
                    key={repo.id}
                    type="button"
                    onClick={() => {
                      selectRepo(repo.id);
                      onSelect(repo.id);
                      handleClose();
                    }}
                    className={[
                      "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
                      repo.id === selectedRepo.id
                        ? "bg-emerald-500/10 hover:bg-emerald-500/15"
                        : "hover:bg-muted/30",
                    ].join(" ")}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={[
                        "truncate font-mono text-xs font-medium",
                        repo.id === selectedRepo.id ? "text-emerald-400" : "",
                      ].join(" ")}>{repo.full_name}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{repo.default_branch}</p>
                    </div>
                    {repo.private && (
                      <span className="shrink-0 rounded bg-purple-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-purple-400">
                        private
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </animated.div>
        </>,
        document.body,
      )}
    </div>
  );
}
